import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2, Wand2, RefreshCw, AlertTriangle, XCircle } from 'lucide-react'
import { startRemake, cancelRemake } from '@/lib/api/amazon-listing'
import { useRemakeStatus, useLatestRemake } from '@/hooks/useRemake'
import { useRemakeEdits } from '@/hooks/useRemakeEdits'
import { useComplianceRules } from '@/hooks/useComplianceRules'
import { RemakeConfirmDialog } from './RemakeConfirmDialog'
import { RemakeVariantTabs } from './RemakeVariantTabs'
import { RemakeToolbar } from './RemakeToolbar'
import { RemakeValidationBanner } from './RemakeValidationBanner'
import { RemakeAcknowledgement } from './RemakeAcknowledgement'

interface RemakeSectionProps {
  jobId: string
  marketplace: string
}

export function RemakeSection({ jobId, marketplace }: RemakeSectionProps) {
  const { t } = useTranslation()
  const [showConfirm, setShowConfirm] = useState(false)
  const queryClient = useQueryClient()
  const { data: status } = useRemakeStatus(jobId)
  const { data: latest } = useLatestRemake(jobId)
  const { rules } = useComplianceRules(marketplace)
  const { edits, updateField, resetField, resetAll, isDirty, save } = useRemakeEdits(latest ?? undefined)
  const [isSaving, setIsSaving] = useState(false)
  const [acknowledged, setAcknowledged] = useState(false)
  const [ackedFor, setAckedFor] = useState<string | undefined>(latest?.id)

  if (latest?.id !== ackedFor) {
    setAckedFor(latest?.id)
    setAcknowledged(false)
  }

  useEffect(() => {
    if (status?.status === 'completed' || status?.status === 'failed') {
      queryClient.invalidateQueries({ queryKey: ['remake', 'latest', jobId] })
    }
  }, [status?.status, jobId, queryClient])

  const { mutate: doStart, isPending: isStarting } = useMutation({
    mutationFn: () => startRemake(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['remake'] })
      setShowConfirm(false)
    },
  })

  const { mutate: doCancel, isPending: isCancelling } = useMutation({
    mutationFn: () => cancelRemake(jobId, status?.remake_id ?? ''),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['remake'] }),
  })

  const handleSave = () => {
    setIsSaving(true)
    save(undefined, { onSettled: () => setIsSaving(false) })
  }

  const isRunning = status?.status === 'pending' || status?.status === 'running'
  const hasUsableResult = !!latest?.text_result && (
    latest.status === 'completed'
    || latest.status === 'validation_failed'
    || latest.status === 'verification_required'
  )
  const isValidationFailed = latest?.status === 'validation_failed'
  const isVerificationRequired = latest?.status === 'verification_required'
  const needsValidationGate = isValidationFailed || isVerificationRequired
  const saveBlocked = isValidationFailed || (isVerificationRequired && !acknowledged)
  const hasFailed = latest?.status === 'failed'
  const isNewVersion = !!latest

  if (isRunning) {
    return (
      <div className="space-y-3">
        <StatusCard icon={<Loader2 className="w-5 h-5 animate-spin text-orange-500" />} text={t(`remake.status.${status?.status ?? 'running'}`)} />
        <button
          onClick={() => doCancel()}
          disabled={isCancelling}
          className={btnCancel}
        >
          {isCancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
          {t('remake.cancel', 'Abbrechen')}
        </button>
      </div>
    )
  }

  if (hasFailed) {
    return (
      <div className="space-y-3">
        <StatusCard icon={<AlertTriangle className="w-5 h-5 text-red-500" />} text={latest.error_message ?? t('remake.status.failed')} />
        <StartButton onClick={() => setShowConfirm(true)} label={t('remake.newVersion')} icon={<RefreshCw className="w-4 h-4" />} />
        <RemakeConfirmDialog open={showConfirm} onConfirm={() => doStart()} onCancel={() => setShowConfirm(false)} isNewVersion isLoading={isStarting} />
      </div>
    )
  }

  if (hasUsableResult && latest?.text_result) {
    return (
      <div className="space-y-4">
        {needsValidationGate && (
          <RemakeValidationBanner
            status={latest.status}
            validation={latest.text_result.validation}
            autoTruncatedFields={latest.text_result.auto_truncated_fields}
          />
        )}
        {isVerificationRequired && (
          <RemakeAcknowledgement acknowledged={acknowledged} onAcknowledge={setAcknowledged} />
        )}
        <div className="flex items-center justify-between">
          <RemakeToolbar
            onSave={handleSave}
            onReset={resetAll}
            isDirty={isDirty}
            isSaving={isSaving}
            saveDisabled={saveBlocked}
            saveDisabledReason={saveBlocked ? t('remake.validation.saveBlockedTooltip') : undefined}
          />
          <StartButton onClick={() => setShowConfirm(true)} label={t('remake.newVersion')} icon={<RefreshCw className="w-4 h-4" />} small />
        </div>
        <RemakeVariantTabs result={latest.text_result} edits={edits} onEditChange={updateField} onResetField={resetField} rules={rules} />
        <RemakeConfirmDialog open={showConfirm} onConfirm={() => doStart()} onCancel={() => setShowConfirm(false)} isNewVersion isLoading={isStarting} />
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="rounded-full bg-orange-100 dark:bg-orange-900/30 p-4 mb-4">
        <Wand2 className="w-8 h-8 text-orange-500" />
      </div>
      <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
        {t('remake.emptyTitle', 'Listing optimieren')}
      </h3>
      <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-6 max-w-md">
        {t('remake.emptyDescription', 'Erstelle eine KI-optimierte Version deines Listings mit verbessertem Titel, Bulletpoints und Beschreibung.')}
      </p>
      <StartButton onClick={() => setShowConfirm(true)} label={t('remake.startButton')} icon={<Wand2 className="w-4 h-4" />} />
      <RemakeConfirmDialog open={showConfirm} onConfirm={() => doStart()} onCancel={() => setShowConfirm(false)} isNewVersion={isNewVersion} isLoading={isStarting} />
    </div>
  )
}

const statusCls = 'flex items-center gap-3 p-4 rounded-xl bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700'
const btnPrimary = 'flex items-center gap-2 px-4 py-2.5 text-sm rounded-xl bg-orange-500 text-white hover:bg-orange-600 font-medium'
const btnSmall = 'flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700'
const btnCancel = 'flex items-center gap-2 px-4 py-2 text-sm rounded-xl border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50'

function StatusCard({ icon, text }: { icon: React.ReactNode; text: string }) {
  return <div className={statusCls}>{icon}<span className="text-sm text-neutral-700 dark:text-neutral-300">{text}</span></div>
}

function StartButton({ onClick, label, icon, small }: { onClick: () => void; label: string; icon: React.ReactNode; small?: boolean }) {
  return <button onClick={onClick} className={small ? btnSmall : btnPrimary}>{icon}{label}</button>
}
